import { useState } from 'react';
import { altaCosturaTestimonials } from '../../../mocks/testimonios';
import { useSupabaseAdmin } from '../../../hooks/useSupabaseAdmin';

interface Testimonial {
  id: number;
  name: string;
  location: string;
  text: string;
  rating: number;
  type: 'alta-costura' | 'arreglos';
  date: string;
  service: string;
}

function testimonialToDb(item: Partial<Testimonial>): Record<string, unknown> {
  return {
    name: item.name,
    location: item.location,
    text: item.text,
    rating: item.rating,
    type: item.type,
    date: item.date,
    service: item.service,
  };
}

function testimonialFromDb(row: Record<string, unknown>): Testimonial {
  return {
    id: row.id as number,
    name: row.name as string,
    location: row.location as string,
    text: row.text as string,
    rating: row.rating as number,
    type: row.type as 'alta-costura' | 'arreglos',
    date: row.date as string,
    service: row.service as string,
  };
}

export default function AdminTestimonials() {
  const { data, add, update, remove, loading } = useSupabaseAdmin<Testimonial>({
    table: 'testimonials',
    initialData: altaCosturaTestimonials,
    mapToDb: testimonialToDb,
    mapFromDb: testimonialFromDb,
  });
  const [editing, setEditing] = useState<Testimonial | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload: Omit<Testimonial, 'id'> = {
      name: fd.get('name') as string,
      location: fd.get('location') as string,
      text: fd.get('text') as string,
      rating: Number(fd.get('rating')),
      type: fd.get('type') as 'alta-costura' | 'arreglos',
      date: fd.get('date') as string,
      service: fd.get('service') as string,
    };
    if (editing) {
      update(editing.id, payload);
      setEditing(null);
    } else {
      add(payload);
      setIsAdding(false);
    }
  };

  return (
    <div className="space-y-6">
      {loading && (
        <div className="flex items-center gap-2 text-brand-gray-500 text-sm">
          <i className="ri-loader-4-line animate-spin" />
          Sincronizando con la nube...
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-serif text-brand-black text-xl">Testimonios</h3>
          <p className="font-sans text-brand-gray-500 text-sm mt-1">
            {data.length} testimonios registrados
          </p>
        </div>
        <button
          onClick={() => { setIsAdding(true); setEditing(null); }}
          className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
        >
          + Nuevo testimonio
        </button>
      </div>

      {(isAdding || editing) && (
        <form
          onSubmit={handleSubmit}
          className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-4"
        >
          <div className="flex items-center justify-between">
            <h4 className="font-serif text-brand-black text-lg">
              {editing ? 'Editar Testimonio' : 'Nuevo Testimonio'}
            </h4>
            <button
              type="button"
              onClick={() => { setEditing(null); setIsAdding(false); }}
              className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Nombre *</label>
              <input name="name" defaultValue={editing?.name} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Ubicación *</label>
              <input name="location" defaultValue={editing?.location} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
          </div>
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Texto del testimonio *</label>
            <textarea
              name="text"
              defaultValue={editing?.text}
              rows={4}
              required
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black resize-none"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Valoración (1-5)</label>
              <input name="rating" type="number" min={1} max={5} defaultValue={editing?.rating || 5} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Tipo</label>
              <select name="type" defaultValue={editing?.type || 'alta-costura'} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black">
                <option value="alta-costura">Alta Costura</option>
                <option value="arreglos">Arreglos</option>
              </select>
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Fecha</label>
              <input name="date" type="date" defaultValue={editing?.date || new Date().toISOString().split('T')[0]} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
          </div>
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Servicio / Pieza</label>
            <input name="service" defaultValue={editing?.service} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button type="submit" className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap">
              Guardar
            </button>
            <button
              type="button"
              onClick={() => { setEditing(null); setIsAdding(false); }}
              className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.map((t) => (
          <div key={t.id} className="bg-white border border-brand-gray-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex gap-0.5">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-brand-black text-xs" />
                ))}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => { setEditing(t); setIsAdding(false); }}
                  className="w-7 h-7 rounded-md bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                >
                  <i className="ri-pencil-line text-xs" />
                </button>
                <button
                  onClick={() => { if (confirm(`¿Eliminar testimonio de ${t.name}?`)) remove(t.id); }}
                  className="w-7 h-7 rounded-md bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-red-600 hover:text-white transition-colors"
                >
                  <i className="ri-delete-bin-line text-xs" />
                </button>
              </div>
            </div>
            <p className="font-sans text-brand-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
              "{t.text}"
            </p>
            <div className="flex items-center justify-between pt-3 border-t border-brand-gray-100">
              <div>
                <p className="font-sans text-brand-black text-sm font-medium">{t.name}</p>
                <p className="font-sans text-brand-gray-400 text-xs">{t.location}</p>
              </div>
              <span className="font-sans text-brand-gray-400 text-[11px] tracking-wider uppercase bg-brand-gray-50 px-2 py-1 rounded-full">
                {t.type === 'alta-costura' ? 'Alta Costura' : 'Arreglos'}
              </span>
            </div>
            <p className="font-sans text-brand-gray-400 text-[11px] mt-2">{t.service}</p>
          </div>
        ))}
      </div>
    </div>
  );
}